import Layout from "../components/Layout";
import { useEffect } from "react";
import Link from "next/link";

import LogoGrid from "../components/LogoGrid";
import TestimonialSection from "../components/TestimonialSection";
import VirtualPricing from "../components/VirtualPricing";
import Image from "../components/Image";
import AntipiracySnipe from "../components/AntipiracySnipe";

function Usage() {
  return (
    <div className="mt-12 sm:mt-24 pt-12 sm:pt-24 bg-gray-100">
      <div className="max-w-screen-xl mx-auto px-6 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl leading-9 font-extrabold text-gray-900 sm:text-4xl sm:leading-10">
            The industry leader in virtual cinema
          </h2>
          <p className="mt-3 text-xl leading-7 text-gray-500 sm:mt-4">
            Among film festivals, arthouses and distributors, Eventive is now
            recognized as the leading online screening platform.
          </p>
        </div>
      </div>
      <div className="mt-10 bg-white">
        <div className="relative">
          <div className="absolute inset-0 h-1/2 bg-gray-100"></div>
          <div className="relative max-w-screen-xl mx-auto px-6 sm:px-6 lg:px-8">
            <div className="max-w-5xl mx-auto">
              <dl className="rounded-lg bg-white shadow-lg sm:grid sm:grid-cols-3">
                <div className="flex flex-col border-b border-gray-100 p-6 text-center sm:border-0 sm:border-r">
                  <dt
                    className="order-2 mt-2 text-lg leading-6 font-medium text-gray-500"
                    id="item-1"
                  >
                    Organizations
                  </dt>
                  <dd
                    className="order-1 text-5xl leading-none font-extrabold text-red-600"
                    aria-describedby="item-1"
                  >
                    700+
                  </dd>
                </div>
                <div className="flex flex-col border-t border-b border-gray-100 p-6 text-center sm:border-0 sm:border-l sm:border-r">
                  <dt className="order-2 mt-2 text-lg leading-6 font-medium text-gray-500">
                    Virtual screenings
                  </dt>
                  <dd className="order-1 text-5xl leading-none font-extrabold text-red-600">
                    35,000+
                  </dd>
                </div>
                <div className="flex flex-col border-t border-gray-100 p-6 text-center sm:border-0 sm:border-l">
                  <dt className="order-2 mt-2 text-lg leading-6 font-medium text-gray-500">
                    Successful streams
                  </dt>
                  <dd className="order-1 text-5xl leading-none font-extrabold text-red-600">
                    2,500,000+
                  </dd>
                </div>
              </dl>
            </div>
          </div>
        </div>
        <TestimonialSection
          logo="mountainfilm"
          body={
            <span>
              When Mountainfilm had to make the quick decision to move our 42nd
              festival online, Eventive was there to guide us and make the
              transition as painless as possible. The festival went off without
              a hitch, and we were pleased to have several thousand new viewers
              who had never been able to attend the festival in the past,
              expanding our reach and mission.
            </span>
          }
          author={"Suzan Beraza"}
          organization={"Festival Director, Mountainfilm Festival"}
        />
      </div>
    </div>
  );
}

export default function Virtual() {
  useEffect(() => {
    if (typeof window !== "undefined") {
      import("imgix.js").then(() => {
        window.imgix.init();
      });
    }
  }, []);

  return (
    <Layout title="Eventive Virtual" noMargin>
      <div className="relative overflow-hidden">
        <div className="hidden sm:block sm:absolute sm:inset-y-0 sm:h-full sm:w-full">
          <div className="relative h-full max-w-screen-xl mx-auto">
            <svg
              style={{ zIndex: -1 }}
              className="absolute right-full transform translate-y-1/4 translate-x-1/4 lg:translate-x-1/2"
              width="404"
              height="784"
              fill="none"
              viewBox="0 0 404 784"
            >
              <defs>
                <pattern
                  id="f210dbf6-a58d-4871-961e-36d5016a0f49"
                  x="0"
                  y="0"
                  width="20"
                  height="20"
                  patternUnits="userSpaceOnUse"
                >
                  <rect
                    x="0"
                    y="0"
                    width="4"
                    height="4"
                    className="text-gray-200"
                    fill="currentColor"
                  />
                </pattern>
              </defs>
              <rect
                width="404"
                height="784"
                fill="url(#f210dbf6-a58d-4871-961e-36d5016a0f49)"
              />
            </svg>
            <svg
              style={{ zIndex: -1 }}
              className="absolute left-full transform -translate-y-3/4 -translate-x-1/4 md:-translate-y-1/2 lg:-translate-x-1/2"
              width="404"
              height="784"
              fill="none"
              viewBox="0 0 404 784"
            >
              <defs>
                <pattern
                  id="5d0dd344-b041-4d26-bec4-8d33ea57ec9b"
                  x="0"
                  y="0"
                  width="20"
                  height="20"
                  patternUnits="userSpaceOnUse"
                >
                  <rect
                    x="0"
                    y="0"
                    width="4"
                    height="4"
                    className="text-gray-200"
                    fill="currentColor"
                  />
                </pattern>
              </defs>
              <rect
                width="404"
                height="784"
                fill="url(#5d0dd344-b041-4d26-bec4-8d33ea57ec9b)"
              />
            </svg>
          </div>
        </div>

        <div className="relative pt-6 pb-12 sm:pb-16 md:pb-20 lg:pb-28 xl:pb-32">
          <div className="mt-10 mx-auto max-w-screen-xl px-6 sm:mt-12 sm:px-6 md:mt-16 lg:mt-20 xl:mt-28">
            <div className="text-center" style={{ marginTop: "15%" }}>
              <div className="flex justify-center mb-12 md:-mt-12 xl:-mx-12 lg:-mt-24">
                <AntipiracySnipe wide />
              </div>
              <Image
                src="/images/brand/virtual-mark.png"
                width="540"
                height="204"
                retina
                className="mx-auto pr-3"
                style={{ maxWidth: "80vw" }}
              />
              <h2 className="text-4xl tracking-tight leading-10 font-extrabold text-gray-900 sm:text-5xl sm:leading-none md:text-6xl">
                Let's bring the movies{" "}
                <br className="hidden sm:block xl:hidden" />
                <span className="text-red-600">online</span>
              </h2>
              <p className="mt-3 max-w-md mx-auto text-base text-gray-500 sm:text-lg md:mt-5 md:text-xl md:max-w-3xl">
                Eventive leads the industry with fully customizable virtual
                screening packages for festivals, arthouses and distributors. We
                take care of security, simplify ticketing, and give you powerful
                visibility into audience data – while your audiences enjoy high
                performance on-demand video and live-streamed events.
              </p>
              <div className="mt-5 max-w-md mx-auto sm:flex sm:justify-center md:mt-8">
                <div className="rounded-md shadow">
                  <Link href="/start?goal=activate">
                    <a className="w-full flex items-center justify-center px-8 py-3 border border-transparent text-base leading-6 font-medium rounded-md text-white bg-red-600 hover:bg-red-500 focus:outline-none focus:shadow-outline-red transition duration-150 ease-in-out md:py-4 md:text-lg md:px-10">
                      Get started
                    </a>
                  </Link>
                </div>
                <div className="mt-3 rounded-md shadow sm:mt-0 sm:ml-3">
                  <Link href="/start?goal=learn_more&message=I'd like to see a demo of Eventive Virtual.">
                    <a className="w-full flex items-center justify-center px-8 py-3 border border-transparent text-base leading-6 font-medium rounded-md text-red-600 bg-white hover:text-red-500 focus:outline-none focus:shadow-outline-blue transition duration-150 ease-in-out md:py-4 md:text-lg md:px-10">
                      Live demo
                    </a>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="pb-16 bg-white overflow-hidden lg:pb-24 pt-8 lg:pt-12">
        <div className="relative max-w-xl mx-auto px-6 sm:px-6 lg:px-8 lg:max-w-screen-xl">
          <svg
            style={{ zIndex: -1 }}
            className="hidden lg:block absolute left-full transform -translate-x-1/2 -translate-y-1/4"
            width="404"
            height="784"
            fill="none"
            viewBox="0 0 404 784"
          >
            <defs>
              <pattern
                id="b1e6e422-73f8-40a6-b5d9-c8586e37e0e7"
                x="0"
                y="0"
                width="20"
                height="20"
                patternUnits="userSpaceOnUse"
              >
                <rect
                  x="0"
                  y="0"
                  width="4"
                  height="4"
                  className="text-gray-200"
                  fill="currentColor"
                />
              </pattern>
            </defs>
            <rect
              width="404"
              height="784"
              fill="url(#b1e6e422-73f8-40a6-b5d9-c8586e37e0e7)"
            />
          </svg>

          <div className="relative">
            <h3 className="text-center text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl sm:leading-10">
              Deliver an incredible experience
            </h3>
            <p className="mt-4 max-w-3xl mx-auto text-center text-xl leading-7 text-gray-500">
              Deliver the{" "}
              <span className="font-semibold">Eventive user experience</span> in
              a complete online screening, film festival, and cinema solution.
              Say goodbye to confusing emails with login instructions or
              activation codes. Now, your audience buys tickets and passes
              directly from you, with proceeds deposited directly into your bank
              account &ndash; using the same customer accounts, passes and
              memberships as your physical events.
            </p>
            <p className="mt-4 max-w-3xl mx-auto text-center text-xl leading-7 text-gray-500">
              And it's{" "}
              <span className="font-semibold">
                fully branded for your event
              </span>
              . Customize everything from your logo to color palette to site
              behavior. Add integrated sponsor ads and custom donation prompts
              at checkout. And even host your virtual event on{" "}
              <span className="font-semibold">your own web domain</span>.
            </p>
          </div>

          <div className="mt-6 -mb-6 lg:mb-0 lg:mt-24">
            <LogoGrid
              logos={[
                { asset: "heartland", label: "Heartland Film" },
                { asset: "afi", label: "American Film Institute" },
                { asset: "mountainfilm", label: "Mountainfilm" },
                { asset: "denverfilm", label: "Denver Film Society" },
                { asset: "magnolia", label: "Magnolia Pictures" },
                { asset: "stl", label: "St. Louis" },
              ]}
            />
          </div>

          <div className="relative mt-12 lg:mt-24 lg:grid lg:grid-cols-2 lg:gap-8 lg:items-center">
            <div className="relative">
              <h4 className="text-2xl leading-8 font-extrabold text-gray-900 tracking-tight sm:text-3xl sm:leading-9">
                The best streaming performance and security
              </h4>
              <p className="mt-3 text-lg leading-7 text-gray-500">
                Eventive combines the best technologies for on-demand and
                livestream content into one easy to use platform, so your
                audience has a great experience every time. Distributors and
                filmmakers alike trust the security protocols built into our
                platform.
              </p>

              <ul className="mt-10">
                <li>
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <div className="flex items-center justify-center h-12 w-12 rounded-md bg-red-500 text-white">
                        <svg
                          fill="none"
                          stroke="currentColor"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          viewBox="0 0 24 24"
                          className="w-6 h-6"
                        >
                          <path d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
                        </svg>
                      </div>
                    </div>
                    <div className="ml-4">
                      <h5 className="text-lg leading-6 font-medium text-gray-900">
                        DRM-protected films on demand
                      </h5>
                      <p className="mt-2 text-base leading-6 text-gray-500">
                        Rights-managed availability windows and geoblocking for
                        each film &ndash; with strict security and encryption
                        protocols. Geoblock access to specific regions, with
                        easy industry and press overrides.{" "}
                        <Link href="/security">
                          <a className="inline-block underline hover:opacity-75">
                            Learn more about security.
                          </a>
                        </Link>
                      </p>
                    </div>
                  </div>
                </li>
                <li className="mt-10">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <div className="flex items-center justify-center h-12 w-12 rounded-md bg-red-500 text-white">
                        <svg
                          fill="none"
                          stroke="currentColor"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          viewBox="0 0 24 24"
                          className="w-6 h-6"
                        >
                          <path d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"></path>
                        </svg>
                      </div>
                    </div>
                    <div className="ml-4">
                      <h5 className="text-lg leading-6 font-medium text-gray-900">
                        Livestream events with secure audience chat
                      </h5>
                      <p className="mt-2 text-base leading-6 text-gray-500">
                        Streamed directly into the Eventive virtual screening
                        &ndash; only ticketed audience members can participate
                        in chat. And livestreams can be combined with On-Demand
                        titles for filmmaker Q&A’s or special events.
                      </p>
                    </div>
                  </div>
                </li>
                <li className="mt-10">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <div className="flex items-center justify-center h-12 w-12 rounded-md bg-red-500 text-white">
                        <svg
                          fill="none"
                          stroke="currentColor"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          viewBox="0 0 24 24"
                          className="w-6 h-6"
                        >
                          <path d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"></path>
                        </svg>
                      </div>
                    </div>
                    <div className="ml-4">
                      <h5 className="text-lg leading-6 font-medium text-gray-900">
                        TV viewing via apps for Apple TV, Roku, Fire TV, Android
                        TV
                      </h5>
                      <p className="mt-2 text-base leading-6 text-gray-500">
                        Make it easy for your audience to have an ideal home
                        viewing experience with native TV apps in addition to
                        the option to screencast from computers and mobile
                        devices.
                      </p>
                    </div>
                  </div>
                </li>
              </ul>
            </div>

            <div className="mt-10 -mx-4 relative lg:mt-0 big-image-right">
              <svg
                style={{ zIndex: -1 }}
                className="hidden lg:block absolute left-1/2 transform -translate-x-1/2 translate-y-16 lg:hidden"
                width="784"
                height="404"
                fill="none"
                viewBox="0 0 784 404"
              >
                <defs>
                  <pattern
                    id="ca9667ae-9f92-4be7-abcb-9e3d727f2941"
                    x="0"
                    y="0"
                    width="20"
                    height="20"
                    patternUnits="userSpaceOnUse"
                  >
                    <rect
                      x="0"
                      y="0"
                      width="4"
                      height="4"
                      className="text-gray-200"
                      fill="currentColor"
                    />
                  </pattern>
                </defs>
                <rect
                  width="784"
                  height="404"
                  fill="url(#ca9667ae-9f92-4be7-abcb-9e3d727f2941)"
                />
              </svg>
              <Image
                src="/images/features/virtualcatalog.png"
                width="900"
                height="647"
                className="shadow-lg"
                feature
                alt=""
              />
            </div>
          </div>

          <svg
            className="hidden lg:block absolute right-full transform translate-x-1/2 translate-y-12"
            width="404"
            height="784"
            fill="none"
            viewBox="0 0 404 784"
          >
            <defs>
              <pattern
                id="64e643ad-2176-4f86-b3d7-f2c5da3b6a6d"
                x="0"
                y="0"
                width="20"
                height="20"
                patternUnits="userSpaceOnUse"
              >
                <rect
                  x="0"
                  y="0"
                  width="4"
                  height="4"
                  className="text-gray-200"
                  fill="currentColor"
                />
              </pattern>
            </defs>
            <rect
              width="404"
              height="784"
              fill="url(#64e643ad-2176-4f86-b3d7-f2c5da3b6a6d)"
            />
          </svg>

          <div className="mt-6 -mb-6 lg:mb-0 lg:mt-24">
            <LogoGrid
              logos={[
                { asset: "calgary", label: "Calgary Film" },
                { asset: "nashville", label: "Nashville Film Festival" },
                { asset: "iffpanama", label: "IFF Panama" },
                { asset: "musicbox", label: "Music Box Films" },
                { asset: "atlanta", label: "Atlanta Film Festival" },
                { asset: "smithsonian", label: "Smithsonian" },
              ]}
            />
          </div>

          <div className="relative mt-12 sm:mt-16 lg:mt-24 z-10">
            <div className="lg:grid lg:grid-flow-row-dense lg:grid-cols-2 lg:gap-8 lg:items-center">
              <div className="lg:col-start-2">
                <h4 className="text-2xl leading-8 font-extrabold text-gray-900 tracking-tight sm:text-3xl sm:leading-9">
                  It’s quick and easy to ingest content and build events
                </h4>
                <p className="mt-3 text-lg leading-7 text-gray-500">
                  Save time and precious resources with Eventive’s one-click
                  ingestion pipeline, and easy event creation with our familiar,
                  intuitive interface.
                </p>

                <ul className="mt-10">
                  <li>
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <div className="flex items-center justify-center h-12 w-12 rounded-md bg-red-500 text-white">
                          <svg
                            className="h-6 w-6"
                            stroke="currentColor"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                            />
                          </svg>
                        </div>
                      </div>
                      <div className="ml-4">
                        <h5 className="text-lg leading-6 font-medium text-gray-900">
                          Adding streaming films is easy
                        </h5>
                        <p className="mt-2 text-base leading-6 text-gray-500">
                          You send a request to the filmmaker or distributor,
                          and they upload a single file, which is automatically
                          converted to all required playback formats for a
                          superior streaming experience.
                        </p>
                      </div>
                    </div>
                  </li>
                  <li className="mt-10">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <div className="flex items-center justify-center h-12 w-12 rounded-md bg-red-500 text-white">
                          <svg
                            fill="none"
                            stroke="currentColor"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            viewBox="0 0 24 24"
                            className="w-6 h-6"
                          >
                            <path d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path>
                          </svg>
                        </div>
                      </div>
                      <div className="ml-4">
                        <h5 className="text-lg leading-6 font-medium text-gray-900">
                          Custom curated shorts programs
                        </h5>
                        <p className="mt-2 text-base leading-6 text-gray-500">
                          Build your programs automatically by selecting from
                          available films in the event editor. A single program
                          unlock provides access to all selected films, served
                          to order in a single player window.
                        </p>
                      </div>
                    </div>
                  </li>
                  <li className="mt-10">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <div className="flex items-center justify-center h-12 w-12 rounded-md bg-red-500 text-white">
                          <svg
                            fill="none"
                            stroke="currentColor"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            viewBox="0 0 24 24"
                            className="w-6 h-6"
                          >
                            <path d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"></path>
                          </svg>
                        </div>
                      </div>
                      <div className="ml-4">
                        <h5 className="text-lg leading-6 font-medium text-gray-900">
                          Start selling streams right away with advance orders
                        </h5>
                        <p className="mt-2 text-base leading-6 text-gray-500">
                          Customers can even reserve movies before they are
                          available to stream &ndash; they’ll get a notification
                          email when their pre-ordered content is ready to
                          watch.
                        </p>
                      </div>
                    </div>
                  </li>
                </ul>
              </div>

              <div className="mt-10 -mx-4 relative lg:mt-0 lg:col-start-1 big-image-left">
                <svg
                  style={{ zIndex: -1 }}
                  className="hidden lg:block absolute left-1/2 transform -translate-x-1/2 translate-y-16 lg:hidden"
                  width="784"
                  height="404"
                  fill="none"
                  viewBox="0 0 784 404"
                >
                  <defs>
                    <pattern
                      id="e80155a9-dfde-425a-b5ea-1f6fadd20131"
                      x="0"
                      y="0"
                      width="20"
                      height="20"
                      patternUnits="userSpaceOnUse"
                    >
                      <rect
                        x="0"
                        y="0"
                        width="4"
                        height="4"
                        className="text-gray-200"
                        fill="currentColor"
                      />
                    </pattern>
                  </defs>
                  <rect
                    width="784"
                    height="404"
                    fill="url(#e80155a9-dfde-425a-b5ea-1f6fadd20131)"
                  />
                </svg>
                <Image
                  src="/images/features/virtualsponsorad.png"
                  width="900"
                  height="647"
                  feature
                  className="shadow-lg"
                  alt=""
                />
              </div>
            </div>
          </div>

          <svg
            className="hidden lg:block absolute left-full transform -translate-x-1/2 -translate-y-1/4"
            width="404"
            height="784"
            fill="none"
            viewBox="0 0 404 784"
          >
            <defs>
              <pattern
                id="b1e6e422-73f8-40a6-b5d9-c8586e37e0e7"
                x="0"
                y="0"
                width="20"
                height="20"
                patternUnits="userSpaceOnUse"
              >
                <rect
                  x="0"
                  y="0"
                  width="4"
                  height="4"
                  className="text-gray-200"
                  fill="currentColor"
                />
              </pattern>
            </defs>
            <rect
              width="404"
              height="784"
              fill="url(#b1e6e422-73f8-40a6-b5d9-c8586e37e0e7)"
            />
          </svg>

          <div className="mt-6 -mb-6 lg:mb-0 lg:mt-24">
            <LogoGrid
              logos={[
                { asset: "sarasota", label: "Sarasota Film Festival" },
                { asset: "a24", label: "A24" },
                { asset: "seriesfest", label: "SeriesFest" },
                { asset: "adelaide", label: "Adelaide Film Festival" },
                { asset: "sonoma", label: "Sonoma Film Festival" },
                { asset: "strand", label: "Strand Releasing" },
              ]}
            />
          </div>

          <div className="relative mt-12 lg:mt-24 lg:grid lg:grid-cols-2 lg:gap-8 lg:items-center z-10">
            <div className="relative">
              <h4 className="text-2xl leading-8 font-extrabold text-gray-900 tracking-tight sm:text-3xl sm:leading-9">
                Everything you need to take all aspects of your organization
                online, including your sponsors
              </h4>
              <p className="mt-3 text-lg leading-7 text-gray-500">
                Give your sponsors and members full recognition and automatic
                benefits using easy customization features and Eventive’s simple
                yet powerful system of pass rules.
              </p>

              <ul className="mt-10">
                <li>
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <div className="flex items-center justify-center h-12 w-12 rounded-md bg-red-500 text-white">
                        <svg
                          fill="none"
                          stroke="currentColor"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          viewBox="0 0 24 24"
                          className="w-6 h-6"
                        >
                          <path d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2"></path>
                        </svg>
                      </div>
                    </div>
                    <div className="ml-4">
                      <h5 className="text-lg leading-6 font-medium text-gray-900">
                        Integrated pre-rolls, trailers, and sponsor messaging
                      </h5>
                      <p className="mt-2 text-base leading-6 text-gray-500">
                        Add a general pre-roll to play automatically before all
                        films and programs, and custom display ads, trailers and
                        sponsor video for each event.
                      </p>
                    </div>
                  </div>
                </li>
                <li className="mt-10">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <div className="flex items-center justify-center h-12 w-12 rounded-md bg-red-500 text-white">
                        <svg
                          fill="none"
                          stroke="currentColor"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          viewBox="0 0 24 24"
                          className="w-6 h-6"
                        >
                          <path d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"></path>
                        </svg>
                      </div>
                    </div>
                    <div className="ml-4">
                      <h5 className="text-lg leading-6 font-medium text-gray-900">
                        Tied to your existing event buckets
                      </h5>
                      <p className="mt-2 text-base leading-6 text-gray-500">
                        Offer a streaming experience seamlessly connected to
                        your film guide, passes, memberships and ticketing.
                      </p>
                    </div>
                  </div>
                </li>
                <li className="mt-10">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <div className="flex items-center justify-center h-12 w-12 rounded-md bg-red-500 text-white">
                        <svg
                          className="h-6 w-6"
                          stroke="currentColor"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M13 10V3L4 14h7v7l9-11h-7z"
                          />
                        </svg>
                      </div>
                    </div>
                    <div className="ml-4">
                      <h5 className="text-lg leading-6 font-medium text-gray-900">
                        Manage it all from a single dashboard
                      </h5>
                      <p className="mt-2 text-base leading-6 text-gray-500">
                        One step ingestion of streaming content, easy streaming
                        event creation, total control of geoblocking,
                        availability windows and capacity, directly from the
                        event dashboard.
                      </p>
                    </div>
                  </div>
                </li>
              </ul>
            </div>

            <div className="mt-10 -mx-4 relative lg:mt-0 big-image-right">
              <svg
                style={{ zIndex: -1 }}
                className="hidden lg:block absolute left-1/2 transform -translate-x-1/2 translate-y-16 lg:hidden"
                width="784"
                height="404"
                fill="none"
                viewBox="0 0 784 404"
              >
                <defs>
                  <pattern
                    id="ca9667ae-9f92-4be7-abcb-9e3d727f2941"
                    x="0"
                    y="0"
                    width="20"
                    height="20"
                    patternUnits="userSpaceOnUse"
                  >
                    <rect
                      x="0"
                      y="0"
                      width="4"
                      height="4"
                      className="text-gray-200"
                      fill="currentColor"
                    />
                  </pattern>
                </defs>
                <rect
                  width="784"
                  height="404"
                  fill="url(#ca9667ae-9f92-4be7-abcb-9e3d727f2941)"
                />
              </svg>
              <Image
                src="/images/features/virtualtraitor.png"
                width="900"
                height="647"
                feature
                className="shadow-lg"
                alt=""
              />
            </div>
          </div>
        </div>
      </div>

      <Usage />

      <div id="pricing" className="mt-12 sm:mt-24 bg-gray-100">
        <div className="pt-12 sm:pt-16 lg:pt-20">
          <div className="max-w-screen-xl mx-auto px-6 sm:px-6 lg:px-8">
            <div className="text-center">
              <h2 className="text-3xl leading-9 font-extrabold text-gray-900 sm:text-4xl sm:leading-10 lg:text-5xl lg:leading-none">
                Simple no-contract pricing
              </h2>
              <p className="mt-4 text-xl leading-7 text-gray-500">
                If you're not satisfied, contact us and we'll send you a full
                refund.
              </p>
            </div>
          </div>
        </div>
        <div className="mt-8 bg-white pb-16 sm:mt-12 sm:pb-20 lg:pb-28">
          <div className="relative">
            <div className="absolute inset-0 h-1/2 bg-gray-100"></div>
            <div className="relative max-w-screen-xl mx-auto px-6 sm:px-6 lg:px-8">
              <VirtualPricing
                title="All-inclusive virtual package"
                description="Our pricing is designed to make Eventive Virtual an accessible and affordable solution for all our partner festivals and cinemas. Use Eventive Virtual on its own, or seamlessly alongside our other products."
                footer={
                  <Link
                    href="/distributors"
                    className="cursor-pointer font-bold"
                  >
                    Are you a distributor?
                  </Link>
                }
                hidePriceDetailDescription={true}
                additionalDescription={
                  <div className="text-sm leading-5">
                    Eventive Partner Ticketing platform fee of 5% + $0.99
                    applies to all paid orders. No ticketing fees on free
                    unlocks. Registered non-profit? Free donations and statement
                    credit available –{" "}
                    <button
                      className="font-medium text-gray-900 focus:outline-none"
                      onClick={() => {
                        Intercom(
                          "showNewMessage",
                          "I'm interested in learning more about non-profit discounts for Eventive Virtual! My name and organization are: "
                        );
                      }}
                    >
                      get in touch
                    </button>
                    .
                  </div>
                }
              />
            </div>
          </div>
        </div>
      </div>

      <div
        style={{ display: "none" }}
        className="fixed bottom-0 inset-x-0 px-6 pb-6 sm:inset-0 sm:p-0 sm:flex sm:items-center sm:justify-center z-30"
      >
        <div className="fixed inset-0 transition-opacity">
          <div className="absolute inset-0 bg-white opacity-75"></div>
        </div>

        <div className="bg-white rounded-lg px-6 pt-5 pb-4 overflow-hidden shadow-xl transform transition-all sm:max-w-lg sm:w-full sm:p-6">
          <div>
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
              <svg
                fill="none"
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                viewBox="0 0 24 24"
                className="w-8 h-8 text-red-600"
              >
                <path d="M13 10V3L4 14h7v7l9-11h-7z"></path>
              </svg>
            </div>
            <div className="mt-3 text-center sm:mt-5">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Eventive for Distributors
              </h3>
              <div className="mt-2">
                <p className="text-base text-gray-500">
                  Contact us to learn more about our Partner Distributor
                  Program, with special rates and a dedicated account manager
                  for distributors who would like to create their own streaming
                  channel partnerships with exhibitors, while also making
                  content available to Eventive Partner Festivals and Cinemas
                  via Eventive Virtual.
                </p>
              </div>
            </div>
          </div>
          <div className="mt-5 sm:mt-6">
            <span className="flex w-full rounded-md shadow-sm">
              <button
                type="button"
                className="inline-flex justify-center w-full rounded-md border border-transparent px-6 py-2 bg-red-600 text-base leading-6 font-medium text-white shadow-sm hover:bg-red-500 focus:outline-none focus:border-red-700 focus:shadow-outline-red transition ease-in-out duration-150 sm:text-sm sm:leading-5"
              >
                Send us a message
              </button>
            </span>
          </div>
        </div>
      </div>
    </Layout>
  );
}
